/**
 * reconcile — `additional_steps` → the authoritative settled step list.
 *
 * The same `additional_steps` array arrives by two completely different routes.
 */

import { getDomainInterpreter } from 'src/content/ChatV2/steps/domains/domainRegistry';
import { EVENT_DOMAIN } from 'src/content/ChatV2/steps/wire/eventType';
import {
  type Step,
  STEP_KIND,
  STEP_STATUS
} from 'src/content/ChatV2/types/steps';

import {
  isInterruptEntryType,
  settledEntryKey,
  stripRunPrefix
} from './stepKey';
import type {
  SettleContext,
  SettledStepEntry,
  StepKey,
  StepStoreSnapshot
} from './types';

/** Narrow an unknown blob to the settled entry array, tolerating both routes. */
export function extractSettledEntries(
  additionalSteps: unknown
): SettledStepEntry[] {
  if (!Array.isArray(additionalSteps)) {
    return [];
  }
  return additionalSteps.filter(
    (entry): entry is SettledStepEntry =>
      Boolean(entry) && typeof entry === 'object'
  );
}

/**
 * Fold the settled entries over a live snapshot.
 */
export function reconcile(
  snapshot: StepStoreSnapshot,
  additionalSteps: unknown
): StepStoreSnapshot {
  const entries = extractSettledEntries(additionalSteps);
  if (entries.length === 0) {
    return snapshot;
  }

  const order: StepKey[] = [];
  const byKey = new Map<StepKey, Step>();
  const runIndex = new Map<string, StepKey>();

  const toolCallRequests = indexToolCallRequests(entries);

  entries.forEach((entry, index) => {
    const identity = settledEntryKey(entry);
    if (!identity) {
      return;
    }
    const { key, domain } = identity;

    const interpreter = isInterruptEntryType(entry.type)
      ? getDomainInterpreter(EVENT_DOMAIN.INTERACTION)
      : getDomainInterpreter(domain);
    const settled = interpreter?.settle?.(
      entry,
      entry.tool_call_id ? toolCallRequests.get(entry.tool_call_id) : undefined
    );
    if (!settled) {
      return;
    }

    const merged = byKey.get(key);
    const live = merged ?? snapshot.byKey.get(key);
    const step: Step = {
      ...(live ?? {
        id: key,
        domain,
        kind: STEP_KIND.TOOL_SEARCH,
        status: STEP_STATUS.DONE
      }),
      ...stripUndefined(settled),
      id: key,
      domain,
      ordinal: merged?.ordinal ?? index,
      version: (live?.version ?? 0) + 1
    };

    const runId = stripRunPrefix(entry.id) ?? live?.runId;
    if (runId) {
      step.runId = runId;
      runIndex.set(runId, key);
    }

    step.detail = interpreter?.detail?.(step) ?? live?.detail;

    if (!merged) {
      order.push(key);
    }
    byKey.set(key, step);
  });

  for (const [key, step] of byKey) {
    if (step.parentKey && !byKey.has(step.parentKey)) {
      byKey.set(key, { ...step, parentKey: undefined });
    }
  }

  appendTerminalStep(order, byKey);

  return { order, byKey, runIndex, version: snapshot.version + 1 };
}

/**
 * Build settled steps from scratch — the HISTORY path.
 */
export function stepsFromSettled(additionalSteps: unknown): Step[] {
  const snapshot = reconcile(
    { order: [], byKey: new Map(), runIndex: new Map(), version: 0 },
    additionalSteps
  );
  return snapshot.order
    .map((key) => snapshot.byKey.get(key))
    .filter((step): step is Step => step != null);
}

/**
 * Key of the synthetic terminal row. Namespaced like every other key, and
 * `terminal` is not a wire domain, so it can never collide with a real step.
 */
export const TERMINAL_STEP_KEY = 'terminal:done';

function appendTerminalStep(order: StepKey[], byKey: Map<StepKey, Step>): void {
  if (order.length === 0 || byKey.has(TERMINAL_STEP_KEY)) {
    return;
  }
  for (const step of byKey.values()) {
    if (step.status === STEP_STATUS.BLOCKED) {
      return;
    }
  }
  order.push(TERMINAL_STEP_KEY);
  byKey.set(TERMINAL_STEP_KEY, {
    id: TERMINAL_STEP_KEY,
    domain: 'terminal',
    kind: STEP_KIND.DONE,
    status: STEP_STATUS.DONE,
    ordinal: order.length,
    version: 1
  });
}

function indexToolCallRequests(
  entries: SettledStepEntry[]
): Map<string, SettleContext> {
  const byCallId = new Map<string, SettleContext>();
  for (const entry of entries) {
    if (!Array.isArray(entry.tool_calls)) {
      continue;
    }
    for (const call of entry.tool_calls) {
      if (!call?.id) {
        continue;
      }
      byCallId.set(call.id, {
        input:
          call.args && typeof call.args === 'object' ? call.args : undefined,
        toolName: typeof call.name === 'string' ? call.name : undefined
      });
    }
  }
  return byCallId;
}

function stripUndefined<T extends object>(value: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value)) {
    if (v !== undefined) {
      out[key] = v;
    }
  }
  return out as Partial<T>;
}
